import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { classNames } from '@/lib/utils';

interface CameraCaptureProps {
    onCapture: (imageBlob: Blob, imageUrl: string) => void;
    onCancel?: () => void;
    className?: string;
}

export function CameraCapture({ onCapture, onCancel, className }: CameraCaptureProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const [isReady, setIsReady] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [capturedImage, setCapturedImage] = useState<{ blob: Blob; url: string } | null>(null);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
    const [isCapturing, setIsCapturing] = useState(false);

    // Parar câmera
    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setIsReady(false);
    }, []);

    // Iniciar câmera
    const startCamera = useCallback(async () => {
        try {
            setError(null);
            setIsReady(false);

            // Parar stream anterior se existir
            stopCamera();

            const mediaStream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                },
                audio: false,
            });

            streamRef.current = mediaStream;

            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;

                // Aguardar o vídeo carregar
                await new Promise<void>((resolve) => {
                    if (videoRef.current) {
                        videoRef.current.onloadedmetadata = () => {
                            videoRef.current?.play().then(() => {
                                setIsReady(true);
                                resolve();
                            }).catch(() => {
                                setIsReady(true);
                                resolve();
                            });
                        };
                    } else {
                        resolve();
                    }
                });
            }
        } catch (err) {
            console.error('Camera error:', err);
            setError('Não foi possível acessar a câmera. Verifique as permissões ou use o botão para selecionar arquivo.');
            setIsReady(false);
        }
    }, [facingMode, stopCamera]);

    // Capturar foto
    const capturePhoto = useCallback(() => {
        if (!videoRef.current || !canvasRef.current || isCapturing) return;

        setIsCapturing(true);

        try {
            const video = videoRef.current;
            const canvas = canvasRef.current;

            // Verificar se o vídeo tem dimensões válidas
            if (video.videoWidth === 0 || video.videoHeight === 0) {
                console.error('Video dimensions are 0');
                setError('Erro ao capturar: vídeo não está pronto');
                setIsCapturing(false);
                return;
            }

            // Configurar canvas com dimensões do vídeo
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;

            // Desenhar frame atual no canvas
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                setIsCapturing(false);
                return;
            }

            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

            // Converter para blob com compressão
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        const url = URL.createObjectURL(blob);
                        setCapturedImage({ blob, url });
                        stopCamera();
                    } else {
                        setError('Erro ao criar imagem');
                    }
                    setIsCapturing(false);
                },
                'image/jpeg',
                0.85
            );
        } catch (err) {
            console.error('Capture error:', err);
            setError('Erro ao capturar foto');
            setIsCapturing(false);
        }
    }, [isCapturing, stopCamera]);

    // Alternar câmera frontal/traseira
    const toggleCamera = useCallback(() => {
        stopCamera();
        setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    }, [stopCamera]);

    // Usar imagem capturada
    const useImage = useCallback(() => {
        if (capturedImage) {
            onCapture(capturedImage.blob, capturedImage.url);
        }
    }, [capturedImage, onCapture]);

    // Refazer foto
    const retakePhoto = useCallback(() => {
        if (capturedImage) {
            URL.revokeObjectURL(capturedImage.url);
            setCapturedImage(null);
        }
        startCamera();
    }, [capturedImage, startCamera]);

    // Usar input de arquivo como fallback
    const handleFileInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const url = URL.createObjectURL(file);
            setCapturedImage({ blob: file, url });
            stopCamera();
        }
    }, [stopCamera]);

    // Iniciar câmera ao montar ou quando facingMode muda
    useEffect(() => {
        // Não iniciar se já tem imagem capturada
        if (capturedImage) return;

        startCamera();

        return () => {
            stopCamera();
        };
    }, [facingMode]);

    // Cleanup ao desmontar
    useEffect(() => {
        return () => {
            if (capturedImage) {
                URL.revokeObjectURL(capturedImage.url);
            }
            stopCamera();
        };
    }, []);

    // Se chegou em imagem capturada, mostrar preview
    if (capturedImage) {
        return (
            <div className={classNames('fixed inset-0 z-50 flex flex-col bg-black', className)}>
                {/* Preview da imagem */}
                <div className="flex-1 min-h-0 overflow-hidden">
                    <img
                        src={capturedImage.url}
                        alt="Foto capturada"
                        className="w-full h-full object-contain"
                    />
                </div>

                {/* Botões */}
                <div className="flex-shrink-0 p-4 bg-black flex gap-3 pb-8">
                    <Button
                        variant="secondary"
                        size="lg"
                        fullWidth
                        onClick={retakePhoto}
                    >
                        Refazer
                    </Button>
                    <Button
                        variant="primary"
                        size="lg"
                        fullWidth
                        onClick={useImage}
                    >
                        Usar Foto
                    </Button>
                </div>
            </div>
        );
    }

    // Se houver erro, mostrar fallback
    if (error) {
        return (
            <div className={classNames('fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900 p-6', className)}>
                <svg
                    className="w-16 h-16 text-content-muted mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                    />
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                </svg>
                <p className="text-white text-center mb-4">{error}</p>

                {/* Input de arquivo como fallback */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileInput}
                    className="hidden"
                />

                <Button
                    variant="primary"
                    size="lg"
                    onClick={() => fileInputRef.current?.click()}
                >
                    Selecionar Arquivo
                </Button>

                <Button
                    variant="ghost"
                    className="mt-3 text-white"
                    onClick={() => {
                        setError(null);
                        startCamera();
                    }}
                >
                    Tentar novamente
                </Button>

                {onCancel && (
                    <Button
                        variant="ghost"
                        className="mt-2 text-white/60"
                        onClick={onCancel}
                    >
                        Cancelar
                    </Button>
                )}
            </div>
        );
    }

    // Tela de câmera
    return (
        <div className={classNames('fixed inset-0 z-50 flex flex-col bg-black', className)}>
            {/* Viewfinder - usa flex-1 e min-h-0 para permitir shrink */}
            <div className="flex-1 min-h-0 relative overflow-hidden">
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="absolute inset-0 w-full h-full object-cover"
                />

                {/* Loading overlay */}
                {!isReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                        <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                )}

                {/* Grid overlay */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="w-full h-full grid grid-cols-3 grid-rows-3">
                        {[...Array(9)].map((_, i) => (
                            <div key={i} className="border border-white/20" />
                        ))}
                    </div>
                </div>

                {/* Top buttons */}
                <div className="absolute top-4 left-4 right-4 flex justify-between">
                    {/* File input fallback button */}
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="p-3 bg-black/50 rounded-full text-white active:bg-black/70"
                        title="Selecionar arquivo"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </button>

                    {/* Flip camera button */}
                    <button
                        onClick={toggleCamera}
                        className="p-3 bg-black/50 rounded-full text-white active:bg-black/70"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileInput}
                    className="hidden"
                />
            </div>

            {/* Controls - flex-shrink-0 garante que não será comprimido */}
            <div className="flex-shrink-0 py-6 px-4 bg-black flex items-center justify-center gap-6 pb-8">
                {onCancel && (
                    <button
                        onClick={onCancel}
                        className="p-3 text-white"
                    >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}

                {/* Capture button */}
                <button
                    onClick={capturePhoto}
                    disabled={!isReady || isCapturing}
                    className={classNames(
                        'w-20 h-20 rounded-full border-4 border-white',
                        'flex items-center justify-center',
                        'transition-transform active:scale-95',
                        (!isReady || isCapturing) && 'opacity-50'
                    )}
                >
                    {isCapturing ? (
                        <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-white" />
                    )}
                </button>

                {/* Placeholder for balance */}
                {onCancel && <div className="w-14" />}
            </div>

            {/* Canvas oculto para captura */}
            <canvas ref={canvasRef} className="hidden" />
        </div>
    );
}

export default CameraCapture;
